import { useState, useEffect } from 'react';
import { PageHeader, Badge, cx, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface ActivityEvent {
  id: string;
  timestamp: string;
  actor: string;
  action: string;
  target: {
    type: string;
    id: string;
    name?: string;
  };
  summary: string;
  source: 'security' | 'compliance';
  changes?: Record<string, [string, string]>;
}

const mockEvents: ActivityEvent[] = [
  {
    id: 'evt-031',
    timestamp: '2026-05-22T10:42:00Z',
    actor: 'Léa Martin',
    action: 'control.edit',
    target: { type: 'control', id: 'EDR01', name: 'Encryption Data at Rest' },
    summary: 'Bumped EDR01 version to v1.2 — added Postgres TDE check',
    source: 'compliance',
    changes: { version: ['v1.1', 'v1.2'] }
  },
  {
    id: 'evt-030',
    timestamp: '2026-05-22T09:11:00Z',
    actor: 'ci-bot',
    action: 'control.run',
    target: { type: 'control', id: 'DEP01', name: 'Dependency Scanning' },
    summary: 'DEP01 ran on captain-api · passing (0 critical CVEs unpatched > 7d)',
    source: 'compliance'
  },
  {
    id: 'evt-029',
    timestamp: '2026-05-22T08:14:00Z',
    actor: 'ci-bot',
    action: 'control.run',
    target: { type: 'control', id: 'AUTH01', name: 'Multi-factor Authentication' },
    summary: 'AUTH01 ran on captain-api PR #247 · failing (3 MFA-bypass findings)',
    source: 'compliance'
  },
  {
    id: 'evt-028',
    timestamp: '2026-05-22T07:00:00Z',
    actor: 'Tom Holland',
    action: 'acceptance.approve',
    target: { type: 'acceptance', id: 'RA-2026-021', name: 'TensorFlow patch delay' },
    summary: 'Approved risk acceptance for DEP01 on captain-ml (tensorflow patch)',
    source: 'compliance',
    changes: { status: ['pending', 'active'] }
  },
  {
    id: 'evt-027',
    timestamp: '2026-05-22T06:30:00Z',
    actor: 'Security Scanner',
    action: 'alert.trigger',
    target: { type: 'alert', id: 'alert-001', name: 'Brute force detection' },
    summary: 'Triggered alert: Multiple failed login attempts detected from 185.220.101.182',
    source: 'security'
  },
  {
    id: 'evt-026',
    timestamp: '2026-05-22T05:00:00Z',
    actor: 'Edouard Franz',
    action: 'review.request',
    target: { type: 'pr', id: 'captain-api/247', name: 'PR #247' },
    summary: 'Requested changes on PR #247 — MFA-bypass finding needs to be addressed',
    source: 'security'
  },
  {
    id: 'evt-025',
    timestamp: '2026-05-22T04:42:00Z',
    actor: 'Tom Holland',
    action: 'rule.edit',
    target: { type: 'rule', id: 'RULE-ENC-TAG', name: 'Encryption tag rule' },
    summary: 'Added PCI Req. 3 to the encryption-at-rest tag-based rule',
    source: 'compliance',
    changes: { 'satisfies.length': ['3', '4'] }
  },
  {
    id: 'evt-024',
    timestamp: '2026-05-22T03:42:00Z',
    actor: 'CrowdStrike',
    action: 'threat.detect',
    target: { type: 'endpoint', id: 'LAPTOP-DEV-003', name: 'Development Laptop' },
    summary: 'Detected and quarantined malware: Trojan.Win32.GenKryptik',
    source: 'security'
  }
];

const actionIcons: Record<string, string> = {
  'control.edit': 'edit',
  'control.run': 'play',
  'acceptance.approve': 'check',
  'alert.trigger': 'bell',
  'review.request': 'message-circle',
  'rule.edit': 'settings',
  'threat.detect': 'shield',
  'user.login': 'user',
  'framework.add': 'plus'
};

const actionColors: Record<string, string> = {
  'control.edit': '#7F56D9',
  'control.run': '#0A69DA',
  'acceptance.approve': '#159D4C',
  'alert.trigger': '#F40B0B',
  'review.request': '#CA9401',
  'rule.edit': '#6B7280',
  'threat.detect': '#F40B0B',
  'user.login': '#0A69DA',
  'framework.add': '#159D4C'
};

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function ActivityFeed() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setEvents(mockEvents);
      setLoading(false);
    };
    
    loadEvents();
  }, []);

  const filteredEvents = events.filter(event => {
    const matchesFilter = filter === 'all' || event.source === filter;
    const matchesSearch = search === '' || 
      event.summary.toLowerCase().includes(search.toLowerCase()) ||
      event.actor.toLowerCase().includes(search.toLowerCase()) ||
      event.target.name?.toLowerCase().includes(search.toLowerCase()) ||
      event.action.toLowerCase().includes(search.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const eventStats = {
    total: events.length,
    security: events.filter(e => e.source === 'security').length,
    compliance: events.filter(e => e.source === 'compliance').length,
    today: events.filter(e => {
      const eventDate = new Date(e.timestamp);
      const today = new Date();
      return eventDate.toDateString() === today.toDateString();
    }).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading activity feed...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Activity Feed"
        subtitle="Comprehensive activity log across security and compliance domains."
        right={
          <div className="flex items-center gap-3">
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:opacity-90">
              <Icon name="settings" size={14} stroke="white" /> Configure
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Total Events</div>
          <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{eventStats.total}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Today</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#0A69DA' }}>{eventStats.today}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Security</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#F40B0B' }}>{eventStats.security}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Compliance</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#7F56D9' }}>{eventStats.compliance}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Icon name="search" size={16} className="text-ink/40" />
          <input
            type="text"
            placeholder="Search activity..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-display text-[14px] text-ink placeholder-ink/40 border-none outline-none bg-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
          >
            <option value="all">All Events</option>
            <option value="security">Security Events</option>
            <option value="compliance">Compliance Events</option>
          </select>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/60">
          Activity Timeline ({filteredEvents.length})
        </div>
        
        {filteredEvents.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mb-4 text-ink/40">
              <Icon name="activity" size={48} />
            </div>
            <h3 className="font-display font-bold text-ink text-[18px] mb-2">No activity found</h3>
            <p className="font-display text-[14px] text-ink/60">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[29px] top-0 bottom-0 w-px bg-[#F7EDDB]" />
            
            <ul>
              {filteredEvents.map((event, index) => (
                <li key={event.id} className="relative flex gap-4 px-5 py-4 hover:bg-[#FCF8F1] border-b border-[#F7EDDB] last:border-0">
                  {/* Timeline dot */}
                  <div 
                    className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full grid place-items-center border-2 border-white"
                    style={{ background: actionColors[event.action] || '#6B7280' }}
                  >
                    <Icon 
                      name={actionIcons[event.action] as any || 'activity'} 
                      size={14} 
                      stroke="white" 
                    />
                  </div>

                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-display font-bold text-ink text-[14px]">
                            {event.actor}
                          </span>
                          <span className="font-display text-[13px] text-ink/60">
                            {event.action.replace('.', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <Badge 
                            tone={event.source === 'security' ? 'crit' : 'draft'} 
                            className="text-[10px]"
                          >
                            {event.source}
                          </Badge>
                        </div>
                        
                        <p className="font-display text-[14px] text-ink/75 mb-2 line-clamp-2">
                          {event.summary}
                        </p>
                        
                        <div className="flex items-center gap-3 text-[12px] text-ink/50">
                          <span>
                            <strong>{event.target.type}:</strong> {event.target.name || event.target.id}
                          </span>
                          {event.changes && (
                            <span>
                              <strong>Changed:</strong> {Object.keys(event.changes).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="font-display text-[12px] text-ink/60">
                          {formatTimeAgo(event.timestamp)}
                        </div>
                        <div className="font-data text-[10px] text-ink/40 mt-0.5">
                          {new Date(event.timestamp).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {/* Show changes if available */}
                    {event.changes && (
                      <div className="mt-3 p-3 rounded-[6px] bg-[#F7EDDB]/50 border border-[#F7EDDB]">
                        <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55 mb-2">Changes</div>
                        <div className="space-y-1">
                          {Object.entries(event.changes).map(([key, [from, to]]) => (
                            <div key={key} className="flex items-center gap-2 text-[12px]">
                              <span className="font-display font-semibold text-ink/70 capitalize">
                                {key.replace(/[._]/g, ' ')}:
                              </span>
                              <span className="font-data text-[#F40B0B] line-through">{from}</span>
                              <Icon name="arrow-right" size={10} className="text-ink/40" />
                              <span className="font-data text-[#159D4C]">{to}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}